(function () {
  const root = document.querySelector('[data-mbp-freischaltung]');
  if (!root) return;

  document.body?.classList.add('mbp-landing-page');

  const form = root.querySelector('[data-mbp-form]');
  const message = root.querySelector('[data-mbp-message]');
  const endpoint = root.dataset.endpoint || '';
  const statusEndpoint = root.dataset.statusEndpoint || '';
  const campaignEndpoint = root.dataset.campaignEndpoint || '';
  const thankUrl = root.dataset.thankUrl || '/pages/mikrobio-freischaltung-danke';
  const roundEndRaw = (root.dataset.roundEnd || '').trim();
  const params = new URLSearchParams(window.location.search || '');
  const isDiscordMode = params.get('community') === 'discord';
  const maxFiles = 3;
  const maxBytes = 20 * 1024 * 1024;
  const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
  const allowedExtensions = /\.(jpe?g|png|webp|heic|heif)$/i;

  let selectedFiles = [];
  let counterTimer = null;
  const counterReveal = {
    visible: false,
    revealed: false,
    current: null,
    target: 39,
    total: 100,
    frame: null
  };

  function setMessage(text, isError) {
    if (!message) return;
    message.textContent = text || '';
    message.classList.toggle('is-error', Boolean(isError));
  }

  function fileSizeLabel(bytes) {
    if (!bytes) return '0 MB';
    return `${(bytes / 1024 / 1024).toFixed(bytes > 1024 * 1024 ? 1 : 2)} MB`;
  }

  function syncInputFiles(input) {
    if (!input || typeof DataTransfer === 'undefined') return;
    const transfer = new DataTransfer();
    selectedFiles.forEach(file => transfer.items.add(file));
    input.files = transfer.files;
  }

  function validateFile(file) {
    const typeOk = allowedTypes.has(file.type) || allowedExtensions.test(file.name || '');
    if (!typeOk) return 'Bitte lade nur JPG, PNG, WEBP oder HEIC hoch.';
    if (file.size > maxBytes) return 'Ein Foto ist größer als 20 MB.';
    return '';
  }

  function setupUpload() {
    const upload = root.querySelector('[data-mbp-upload]');
    if (!upload) return;
    const input = upload.querySelector('[data-mbp-upload-input]');
    const button = upload.querySelector('[data-mbp-upload-button]');
    const dropzone = upload.querySelector('[data-mbp-upload-dropzone]');
    const preview = upload.querySelector('[data-mbp-upload-preview]');
    const count = upload.querySelector('[data-mbp-upload-count]');
    const error = upload.querySelector('[data-mbp-upload-error]');
    if (!input || !button || !dropzone || !preview || !count || !error) return;

    function setUploadError(text) {
      error.textContent = text || '';
    }

    function renderPreview() {
      count.textContent = `${selectedFiles.length}/${maxFiles} Fotos hochgeladen`;
      preview.innerHTML = selectedFiles.map((file, index) => {
        const isPreviewable = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
        const src = isPreviewable ? URL.createObjectURL(file) : '';
        const thumb = src
          ? `<img src="${src}" alt="">`
          : `<div class="mbp-upload-item__thumb">${(file.name.split('.').pop() || 'IMG').slice(0, 4).toUpperCase()}</div>`;
        return `<div class="mbp-upload-item" data-upload-index="${index}">
          ${thumb}
          <div>
            <strong>${file.name}</strong>
            <small>${fileSizeLabel(file.size)}</small>
          </div>
          <button type="button" data-remove-upload="${index}">Entfernen</button>
        </div>`;
      }).join('');
      preview.querySelectorAll('[data-remove-upload]').forEach(removeButton => {
        removeButton.addEventListener('click', () => {
          const index = Number(removeButton.dataset.removeUpload);
          selectedFiles = selectedFiles.filter((_, fileIndex) => fileIndex !== index);
          syncInputFiles(input);
          renderPreview();
        });
      });
    }

    function addFiles(fileList) {
      const incoming = Array.from(fileList || []);
      if (!incoming.length) return;
      if (selectedFiles.length + incoming.length > maxFiles) {
        setUploadError(`Bitte lade maximal ${maxFiles} Fotos hoch.`);
        return;
      }
      const validationError = incoming.map(validateFile).find(Boolean);
      if (validationError) {
        setUploadError(validationError);
        return;
      }
      selectedFiles = selectedFiles.concat(incoming);
      setUploadError('');
      syncInputFiles(input);
      renderPreview();
    }

    button.addEventListener('click', () => input.click());
    dropzone.addEventListener('click', event => {
      if (event.target === button) return;
      input.click();
    });
    input.addEventListener('change', () => addFiles(input.files));
    ['dragenter', 'dragover'].forEach(type => {
      dropzone.addEventListener(type, event => {
        event.preventDefault();
        dropzone.classList.add('is-dragover');
      });
    });
    ['dragleave', 'drop'].forEach(type => {
      dropzone.addEventListener(type, event => {
        event.preventDefault();
        dropzone.classList.remove('is-dragover');
      });
    });
    dropzone.addEventListener('drop', event => addFiles(event.dataTransfer?.files));
    renderPreview();
  }

  function pad(value) {
    return String(Math.max(0, Number(value || 0))).padStart(2, '0');
  }

  function prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  function normalizeCampaignPayload(payload = {}) {
    if (payload.counter_enabled === false) return null;
    if (payload.total_slots !== undefined || payload.remaining_slots !== undefined) {
      return {
        status: payload.status || payload.campaign_status || 'active',
        round_label: payload.round_label || 'Aktuelle Runde',
        campaign_end_at: payload.campaign_end_at || roundEndRaw || '',
        total_slots: Number(payload.total_slots || 0),
        used_slots: Number(payload.used_slots || 0),
        baseline_reserved_slots: Number(payload.baseline_reserved_slots || 0),
        allocated_slots: Number(payload.allocated_slots || 0),
        remaining_slots: Number(payload.remaining_slots ?? payload.total_slots ?? 0)
      };
    }
    const campaign = payload.campaign || {};
    const quota = Number(campaign.total_slots || payload.quota || 100);
    const used = Number(campaign.used_slots ?? payload.codes_sent ?? 0);
    return {
      status: campaign.status || campaign.campaign_status || 'active',
      round_label: campaign.round_label || 'Aktuelle Runde',
      campaign_end_at: campaign.campaign_end_at || roundEndRaw || '',
      total_slots: quota,
      used_slots: used,
      baseline_reserved_slots: Number(campaign.baseline_reserved_slots ?? payload.baseline_reserved_slots ?? 0),
      allocated_slots: Number(campaign.allocated_slots ?? payload.allocated_slots ?? used),
      remaining_slots: Number(campaign.remaining_slots ?? payload.remaining ?? Math.max(0, quota - used))
    };
  }

  function setCountdown(endValue) {
    const counter = root.querySelector('[data-mbp-countdown]');
    const countdown = root.querySelector('[data-mbp-countdown-units]');
    if (!counter || !countdown) return false;
    const end = endValue ? Date.parse(endValue) : NaN;
    if (counterTimer) {
      window.clearInterval(counterTimer);
      counterTimer = null;
    }
    if (Number.isNaN(end) || end <= Date.now()) {
      countdown.hidden = true;
      counter.classList.remove('is-countdown-active');
      return false;
    }

    const daysEl = countdown.querySelector('[data-days]');
    const hoursEl = countdown.querySelector('[data-hours]');
    const minutesEl = countdown.querySelector('[data-minutes]');
    const secondsEl = countdown.querySelector('[data-seconds]');

    const tick = () => {
      const diff = Math.max(0, end - Date.now());
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff / 3600000) % 24);
      const minutes = Math.floor((diff / 60000) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);
      if (diff <= 0) {
        countdown.hidden = true;
        counter.classList.remove('is-countdown-active');
        counter.classList.add('is-ended');
        window.clearInterval(counterTimer);
      }
    };

    countdown.hidden = false;
    counter.classList.add('is-countdown-active');
    tick();
    counterTimer = window.setInterval(tick, 1000);
    return true;
  }

  function setProgressValue(progress, value, total) {
    if (!progress) return;
    const progressValue = total ? `${Math.round((Math.max(0, value) / total) * 100)}%` : '0%';
    progress.style.setProperty('--mbp-progress', progressValue);
    progress.style.width = progressValue;
  }

  function setSlotsText(slotsLabel, value, total) {
    if (!slotsLabel) return;
    if (total) {
      slotsLabel.textContent = `Noch ${value} von ${total} Freischaltungen`;
    } else {
      slotsLabel.textContent = 'Limitiertes Kontingent für Neukunden';
    }
  }

  function animateSlots(slotsLabel, progress, from, to, total) {
    if (counterReveal.frame) window.cancelAnimationFrame(counterReveal.frame);
    const start = Math.max(0, Number(from || 0));
    const target = Math.max(0, Number(to || 0));
    if (prefersReducedMotion() || start === target) {
      counterReveal.current = target;
      setSlotsText(slotsLabel, target, total);
      setProgressValue(progress, target, total);
      return;
    }
    const startedAt = performance.now();
    const duration = 10000;
    const step = now => {
      const progressRatio = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progressRatio, 3);
      const value = Math.round(start + (target - start) * eased);
      counterReveal.current = value;
      setSlotsText(slotsLabel, value, total);
      setProgressValue(progress, value, total);
      if (progressRatio < 1) {
        counterReveal.frame = window.requestAnimationFrame(step);
      } else {
        counterReveal.current = target;
        setSlotsText(slotsLabel, target, total);
        setProgressValue(progress, target, total);
      }
    };
    counterReveal.frame = window.requestAnimationFrame(step);
  }

  function maybeRevealCounter() {
    const counter = root.querySelector('[data-mbp-countdown]');
    const slotsLabel = root.querySelector('[data-mbp-slots-label]');
    const progress = root.querySelector('[data-mbp-counter-progress]');
    if (!counter || !slotsLabel || !counterReveal.visible || counterReveal.revealed) return;
    const total = Math.max(0, Number(counterReveal.total || 0));
    const target = Math.max(0, Math.min(total || counterReveal.target, Number(counterReveal.target || 0)));
    counterReveal.revealed = true;
    counter.classList.add('is-revealing');
    animateSlots(slotsLabel, progress, total || target, target, total);
  }

  function setupCounterReveal() {
    const counter = root.querySelector('[data-mbp-countdown]');
    if (!counter) return;
    const reveal = () => {
      counterReveal.visible = true;
      maybeRevealCounter();
    };
    if (!('IntersectionObserver' in window)) {
      reveal();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      reveal();
      observer.disconnect();
    }, { threshold: 0.25, rootMargin: '0px 0px -8% 0px' });
    observer.observe(counter);
  }

  function setCounter(payload = {}) {
    const data = normalizeCampaignPayload(payload);
    if (!data) return;
    const counter = root.querySelector('[data-mbp-countdown]');
    const roundLabel = root.querySelector('[data-mbp-round-label]');
    const roundStatus = root.querySelector('[data-mbp-round-status]');
    const slotsLabel = root.querySelector('[data-mbp-slots-label]');
    const progress = root.querySelector('[data-mbp-counter-progress]');
    if (!counter || !slotsLabel) return;

    const total = Math.max(0, Number(data.total_slots || 0));
    const remaining = Math.max(0, Math.min(total || Number(data.remaining_slots || 0), Number(data.remaining_slots || 0)));
    const end = data.campaign_end_at ? Date.parse(data.campaign_end_at) : NaN;
    const ended = data.status === 'ended' || (!Number.isNaN(end) && end <= Date.now());
    const waitlist = data.status === 'waitlist' || remaining <= 0;

    if (roundLabel) roundLabel.textContent = ended ? 'Diese Runde' : (data.round_label || 'Aktuelle Runde');
    if (roundStatus) {
      roundStatus.textContent = ended
        ? 'ist beendet'
        : data.status === 'paused'
          ? 'pausiert'
          : waitlist
            ? 'Warteliste geöffnet'
            : 'läuft jetzt';
    }

    if (ended || waitlist) {
      counterReveal.revealed = true;
      counterReveal.current = remaining;
      counterReveal.target = remaining;
      counterReveal.total = total;
      slotsLabel.textContent = 'Warteliste geöffnet';
    } else {
      counterReveal.target = remaining;
      counterReveal.total = total;
      if (counterReveal.revealed) {
        const from = counterReveal.current === null ? remaining : counterReveal.current;
        animateSlots(slotsLabel, progress, from, remaining, total);
      } else {
        setSlotsText(slotsLabel, total, total);
        setProgressValue(progress, total, total);
        maybeRevealCounter();
      }
    }

    const showProgress = Boolean(total && remaining > 0 && remaining < total);
    counter.classList.toggle('has-progress', showProgress);
    counter.classList.toggle('is-waitlist', waitlist);
    counter.classList.toggle('is-ended', ended);
    if (ended || waitlist) setProgressValue(progress, remaining, total);
    setCountdown(ended ? '' : data.campaign_end_at);
    window.requestAnimationFrame(() => counter.classList.add('is-loaded'));
  }

  async function loadCounter() {
    const endpoints = [campaignEndpoint, statusEndpoint].filter(Boolean);
    if (!endpoints.length) {
      setCounter({ total_slots: 100, baseline_reserved_slots: 61, remaining_slots: 39, campaign_end_at: roundEndRaw });
      return;
    }
    let loaded = false;
    for (const url of endpoints) {
      if (loaded) break;
      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) continue;
        const data = normalizeCampaignPayload(payload);
        if (!data) return;
        setCounter(data);
        loaded = true;
      } catch {
        // Try the compatibility endpoint before falling back to the default visible state.
      }
    }
    if (!loaded) setCounter({ total_slots: 100, baseline_reserved_slots: 61, remaining_slots: 39, campaign_end_at: roundEndRaw });
  }

  function setupCountdown() {
    try {
      setCounter({ total_slots: 100, baseline_reserved_slots: 61, remaining_slots: 39, campaign_end_at: roundEndRaw });
    } catch {
      // The counter is progressive enhancement; the static label remains readable.
    }
  }

  function setupStickyCta() {
    const sticky = root.querySelector('.mbp-sticky-cta');
    const formSection = root.querySelector('#mbp-form');
    if (!sticky) return;
    const update = () => {
      const formRect = formSection?.getBoundingClientRect();
      const formInView = formRect && formRect.top < window.innerHeight - 80 && formRect.bottom > 120;
      const nearPageEnd = window.scrollY + window.innerHeight > document.documentElement.scrollHeight - 260;
      root.classList.toggle('is-sticky-active', window.scrollY > 420 && !formInView && !nearPageEnd);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  function setupHeroSlider() {
    const slider = root.querySelector('[data-mbp-hero-slider]');
    if (!slider) return;
    const slides = Array.from(slider.querySelectorAll('[data-mbp-hero-slide]'));
    const dots = Array.from(slider.querySelectorAll('[data-mbp-hero-dot]'));
    if (slides.length < 2 || dots.length !== slides.length) return;

    const delay = 6500;
    let activeIndex = 0;
    let timer = null;
    let paused = false;
    const reduceMotion = prefersReducedMotion();

    function setActive(index) {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === activeIndex);
      });
      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }

    function start() {
      if (reduceMotion || paused || timer) return;
      timer = window.setInterval(() => setActive(activeIndex + 1), delay);
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        setActive(index);
        stop();
        start();
      });
    });

    slider.addEventListener('pointerenter', () => {
      paused = true;
      stop();
    });
    slider.addEventListener('pointerleave', () => {
      paused = false;
      start();
    });
    slider.addEventListener('focusin', () => {
      paused = true;
      stop();
    });
    slider.addEventListener('focusout', () => {
      paused = false;
      start();
    });

    setActive(0);
    start();
  }

  function setupDiscordMode() {
    const participationInput = form?.querySelector('input[name="mbp_participation_type"]');
    const sourceInput = form?.querySelector('input[name="mbp_source"]');
    const banner = form?.querySelector('[data-mbp-discord-banner]');
    const discordField = form?.querySelector('[data-mbp-discord-field]');
    const discordName = form?.querySelector('input[name="discord_name"]');
    const normalConsent = form?.querySelector('[data-mbp-normal-consent]');
    const normalConsentInput = normalConsent?.querySelector('input');
    const discordConsent = form?.querySelector('[data-mbp-discord-consent]');
    const discordConsentInput = discordConsent?.querySelector('input');
    const normalNote = form?.querySelector('[data-mbp-normal-consent-note]');
    const discordNote = form?.querySelector('[data-mbp-discord-consent-note]');
    const subline = form?.querySelector('[data-mbp-form-subline]');

    root.classList.toggle('is-discord-mode', isDiscordMode);
    if (participationInput) participationInput.value = isDiscordMode ? 'discord_special' : 'new_customer';
    if (sourceInput) sourceInput.value = isDiscordMode ? 'discord' : 'landingpage';
    if (banner) {
      banner.hidden = !isDiscordMode;
      banner.style.display = isDiscordMode ? '' : 'none';
    }
    if (discordField) {
      discordField.hidden = !isDiscordMode;
      discordField.style.display = isDiscordMode ? '' : 'none';
    }
    if (discordName) {
      discordName.required = isDiscordMode;
      discordName.disabled = !isDiscordMode;
    }
    if (normalConsent) {
      normalConsent.hidden = isDiscordMode;
      normalConsent.style.display = isDiscordMode ? 'none' : '';
    }
    if (normalConsentInput) {
      normalConsentInput.required = !isDiscordMode;
      normalConsentInput.disabled = isDiscordMode;
    }
    if (discordConsent) {
      discordConsent.hidden = !isDiscordMode;
      discordConsent.style.display = isDiscordMode ? '' : 'none';
    }
    if (discordConsentInput) {
      discordConsentInput.required = isDiscordMode;
      discordConsentInput.disabled = !isDiscordMode;
    }
    if (normalNote) {
      normalNote.hidden = isDiscordMode;
      normalNote.style.display = isDiscordMode ? 'none' : '';
    }
    if (discordNote) {
      discordNote.hidden = !isDiscordMode;
      discordNote.style.display = isDiscordMode ? '' : 'none';
    }
    if (subline) subline.textContent = isDiscordMode
      ? (subline.dataset.discordCopy || subline.textContent)
      : (subline.dataset.defaultCopy || subline.textContent);
  }

  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(name => {
    const input = form?.querySelector(`input[name="${name}"]`);
    if (input) input.value = params.get(name) || '';
  });

  setupDiscordMode();
  setupUpload();
  setupHeroSlider();
  setupStickyCta();
  setupCounterReveal();
  setupCountdown();
  loadCounter();
  window.setInterval(loadCounter, 60000);

  form?.addEventListener('submit', async event => {
    event.preventDefault();
    setMessage('');

    const uploadInput = form.querySelector('[data-mbp-upload-input]');
    const uploadError = form.querySelector('[data-mbp-upload-error]');
    if (selectedFiles.length < 1) {
      if (uploadError) uploadError.textContent = 'Bitte lade mindestens ein Setup-Foto hoch.';
      uploadInput?.focus();
      return;
    }
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (!endpoint) {
      setMessage('Das Formular ist noch nicht verbunden. Bitte versuche es später erneut.', true);
      return;
    }

    const submitButton = form.querySelector('[data-mbp-submit]');
    const originalText = submitButton?.textContent || '';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Einreichung wird gesendet...';
    }
    setMessage('Deine Einreichung wird übertragen...');

    try {
      syncInputFiles(uploadInput);
      const response = await fetch(endpoint, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Die Einreichung konnte nicht verarbeitet werden.');
      window.location.assign(thankUrl);
    } catch (error) {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      }
      setMessage(error.message || 'Die Einreichung konnte nicht verarbeitet werden.', true);
    }
  });
})();
